/**
 * Replaces trademark-like tokens with <sup>…</sup>.
 * If the input contains a <sup> tag already, returns the input unchanged.
 * Supports plain text and HTML. For HTML, only text nodes are processed (tags left intact).
 */
export type SuperscriptOptions = {
  isRichText?: boolean; // true if the string contains HTML from RTE
};

const ENTITY_NORMALIZATIONS: Record<string, string> = {
  // Registered mark (standard entities)
  '&reg;': '®',
  '&#174;': '®',
  '&#x00AE;': '®',

  // Trademark (standard entities)
  '&trade;': '™',
  '&#8482;': '™',
  '&#x2122;': '™',

  // Service mark (no standard named entity; we accept &sm; if present)
  '&sm;': '℠', // non-standard, normalized to ℠
  '&#8480;': '℠',
  '&#x2120;': '℠',
};

const normalizeEntities = (input: string) =>
  input.replace(
    /&(reg|trade|sm);|&#(174|8480|8482);|&#x(00AE|2120|2122);/gi,
    (match) => ENTITY_NORMALIZATIONS[match.toLowerCase()] ?? match
  );

// Replace only target tokens in a text node (plain text)
const superscriptTextTokens = (text: string) =>
  text
    // Normalize common author inputs like (R), (TM), (SM)
    .replace(/\((r)\)/gi, '®')
    .replace(/\((tm)\)/gi, '™')
    .replace(/\((sm)\)/gi, '℠')
    // Now wrap only the eligible characters in <sup>
    .replace(/®/g, '<sup>®</sup>')
    .replace(/™/g, '<sup>™</sup>')
    .replace(/℠/g, '<sup>℠</sup>');

/**
 * Process a plain-text string (escaping HTML first) or an HTML string (processing outside tags).
 * If any <sup> tag is found, the function returns the input unchanged to avoid double-wrapping.
 */
export function applySuperscripts(input?: string | null, opts: SuperscriptOptions = {}): string {
  if (!input) {
    return '';
  }
  const { isRichText = false } = opts;

  // If authors already used <sup>, leave the string alone.
  if (/<\s*sup\b/i.test(input)) {
    return input;
  }

  // Normalize entities first (e.g., &reg; -> ®, &trade; -> ™, &sm; -> ℠)
  const normalized = normalizeEntities(input);

  if (!isRichText) {
    // Escape HTML for plain text, then apply replacements
    const escaped = normalized
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
    return superscriptTextTokens(escaped);
  }

  // For rich HTML: split by tags and only transform text segments (keep tags intact)
  return normalized
    .split(/(<[^>]+>)/g)
    .map((segment) => (segment.startsWith('<') ? segment : superscriptTextTokens(segment)))
    .join('');
}
